/**
 * SubsTab — Modal Tab 1: Subs & Inbox.
 * canonical: S2 lines 1493–1636 (.pm-sub-row, .pm-inbox, .pm-ibx-*)
 *
 * Live data:
 *   - fetchProjectVendors() → sub rows from project_vendors (v4 invite flow)
 *   - fetchProjectInbox()   → inbox items (all statuses)
 *
 * Stealth bug fix (PR-V4-PROJECT-HUB-WIRING): the endpoint returns ALL trades
 * regardless of status. Previously no client-side filter existed, so removed/
 * inactive subs could silently appear in the active list. Now filtered correctly:
 *   activeTrades   = status 'active' | 'invited'
 *   archivedTrades = everything else (status 'inactive' | 'removed' | unknown)
 *
 * Clicking "→ View" on a sub row navigates to SubDetail nested view.
 * Clicking an inbox item navigates to DocReview nested view.
 * No Tailwind — all styles from tokens.css.
 */

import { useState, useEffect } from 'react'
import { useQuery }        from '@tanstack/react-query'
import { fetchProjectVendors, fetchProjectInbox, fetchSubPayments } from '../../../api/projects'
import type { ProjectTrade, HubInboxItem }       from '../../../api/types'
import InviteSubModal                             from './InviteSubModal'
import { statusRowClass, matchesInboxFilter, inboxStatusChip, subPaymentStatusChip } from './inboxStatus'
import TrustScoreBadge                            from '../../../components/trust/TrustScoreBadge'
import JobcostErrorBoundary                       from './JobcostErrorBoundary'
import SubCommitmentCard                          from './SubCommitmentCard'
import ProjectVaultRow                            from './ProjectVaultRow'
import HubDocRowActions from '../../../components/docs/HubDocRowActions'
import HubDocDeleteConfirm from '../../../components/docs/HubDocDeleteConfirm'
import DocTypeEditModal from '../../../components/docs/DocTypeEditModal'
import { useHubUploadRowActions } from '../../../hooks/useHubUploadRowActions'

// ── helpers ────────────────────────────────────────────────────────────────

const TRADE_COLORS = [
  'var(--clr-slate-800)','var(--clr-teal-700)','var(--clr-red-700)','var(--clr-sky-700)',
  'var(--clr-sky-600)','var(--clr-amber-700)','var(--clr-green-800)','var(--clr-indigo-700)',
]
function tradeColor(id: number): string {
  return TRADE_COLORS[id % TRADE_COLORS.length]
}
function initials(name: string | null): string {
  if (!name) return '?'
  return name.split(/\s+/).slice(0,2).map(w => w[0].toUpperCase()).join('')
}

function docTypeIcon(type: string): string {
  if (type === 'invoice')      return '₋'
  if (type === 'lien_waiver')  return '§'
  if (type === 'insurance')    return '☂'
  if (type === 'w9')           return 'W'
  if (type === 'change_order') return '±'
  return '⊡'
}
function docTypeMark(type: string): string {
  if (type === 'invoice')      return 'invoice'
  if (type === 'change_order') return 'co'
  return 'doc'
}
function fmtAmt(raw: string | null | undefined): string {
  const n = parseFloat(raw ?? '0')
  if (!n) return '—'
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
}
function fmtAge(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const h    = Math.floor(diff / 3_600_000)
  if (h < 1)  return 'now'
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

type InboxFilter = 'all' | 'pending' | 'approved' | 'rejected'

// ── component ──────────────────────────────────────────────────────────────

interface Props {
  projectId:       number
  jobcostOn?:      boolean
  /** From ?filter=pending|approved|rejected|all deep link (ARIA / notifications). */
  initialInboxFilter?: InboxFilter
  onViewSub:       (trade: ProjectTrade) => void
  onViewInboxItem: (item: HubInboxItem) => void
}

export default function SubsTab({
  projectId,
  jobcostOn = false,
  initialInboxFilter,
  onViewSub,
  onViewInboxItem,
}: Props) {
  const [inboxFilter,     setInboxFilter]     = useState<InboxFilter>(
    initialInboxFilter && ['all', 'pending', 'approved', 'rejected'].includes(initialInboxFilter)
      ? initialInboxFilter
      : 'all',
  )
  const [showArchived,    setShowArchived]    = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const actions = useHubUploadRowActions(projectId)

  useEffect(() => {
    if (
      initialInboxFilter
      && ['all', 'pending', 'approved', 'rejected'].includes(initialInboxFilter)
    ) {
      setInboxFilter(initialInboxFilter)
    }
  }, [initialInboxFilter, projectId])

  const { data: trades = [], isPending: tradesLoading } = useQuery({
    queryKey:  ['project-vendors', projectId],
    queryFn:   () => fetchProjectVendors(projectId),
    staleTime: 30_000,
  })

  const { data: inbox = [], isPending: inboxLoading } = useQuery({
    queryKey:            ['project-inbox', projectId],
    queryFn:             () => fetchProjectInbox(projectId),
    staleTime:           5_000,
    refetchOnWindowFocus: true,
    refetchInterval:     60_000, // light poll so new sub uploads appear on their own
  })

  const { data: subPayments } = useQuery({
    queryKey:  ['sub-payments', projectId],
    queryFn:   () => fetchSubPayments(projectId),
    staleTime: 10_000,
    refetchInterval: 60_000,
  })
  const payRows = subPayments?.data ?? []

  // Client-side status split — endpoint returns all statuses (stealth bug fix)
  const activeTrades   = trades.filter(t => t.status === 'active' || t.status === 'invited')
  const archivedTrades = trades.filter(t => t.status !== 'active' && t.status !== 'invited')

  const filteredInbox = inbox.filter(item => matchesInboxFilter(item.status, inboxFilter))

  const pendingCount  = inbox.filter(i => i.status === 'pending' || i.status === 'changes_requested').length
  const approvedCount = inbox.filter(i => i.status === 'approved').length
  const rejectedCount = inbox.filter(i => i.status === 'rejected').length

  // ── Sub row renderer ──────────────────────────────────────────────────────

  function SubRow({ trade, dimmed, testId }: { trade: ProjectTrade; dimmed?: boolean; testId?: string }) {
    return (
      <div>
        <button
          type="button"
          className="pm-sub-row"
          onClick={() => onViewSub(trade)}
          style={dimmed ? { opacity: 0.55 } : undefined}
          data-testid={testId}
        >
          <div
            className="pm-sub-av"
            style={{ background: tradeColor(trade.id) }}
          >
            {initials(trade.company_name ?? trade.name)}
          </div>
          <div>
            <div className="pm-sub-nm">
              {trade.company_name ?? trade.name}
            </div>
            <div className="pm-sub-meta">
              {trade.name}
              {trade.contact_name && <> · {trade.contact_name}</>}
              {dimmed && <> · <span style={{ color: 'var(--ink4)' }}>Archived</span></>}
            </div>
          </div>
          <div>
            {trade.vendor_user_id ? (
              <TrustScoreBadge vendorUserId={trade.vendor_user_id} size="sm" />
            ) : null}
          </div>
          <div className="pm-sub-arrow">›</div>
        </button>
        {jobcostOn && (
          <JobcostErrorBoundary>
            <SubCommitmentCard
              projectId={projectId}
              vendorId={trade.vendor_id}
              companyName={trade.company_name}
              enabled={jobcostOn}
            />
          </JobcostErrorBoundary>
        )}
      </div>
    )
  }

  return (
    <div>
      {/* ── SUBS ── */}
      <div className="pm-section-h" style={{ marginBottom: 10 }}>
        <div>
          <div className="pm-section-tt">Subcontractors on this project</div>
          <div className="pm-section-st">{activeTrades.length} trades active</div>
        </div>
        <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
          <button
            className="btn b-dk btn-sm"
            onClick={() => setShowInviteModal(true)}
            data-testid="subs-invite-btn"
            type="button"
          >
            + Invite sub
          </button>
        </div>
      </div>

      <div className="panel" style={{ marginBottom: 18, overflow: 'hidden' }}>
        {tradesLoading && (
          <div style={{ padding: '18px 16px', fontSize: 12, color: 'var(--ink4)' }}>
            Loading subs…
          </div>
        )}
        {!tradesLoading && activeTrades.length === 0 && archivedTrades.length === 0 && (
          <div style={{ padding: '18px 16px', fontSize: 12, color: 'var(--ink4)' }}>
            No subs added to this project yet.
          </div>
        )}

        {/* Active trades */}
        {activeTrades.map(trade => (
          <SubRow key={trade.id} trade={trade} />
        ))}

        {/* Archived section — only shown when archived exist */}
        {!tradesLoading && archivedTrades.length > 0 && (
          <>
            <button
              type="button"
              className="pm-ibx-more"
              style={{ userSelect: 'none' }}
              onClick={() => setShowArchived(prev => !prev)}
              data-testid="subs-archived-toggle"
            >
              {showArchived
                ? `Hide archived (${archivedTrades.length}) ↑`
                : `View archived (${archivedTrades.length}) ↓`}
            </button>
            {showArchived && (
              <>
                <div style={{
                  borderTop: '1px dashed var(--line)',
                  margin: '0 16px',
                  padding: '8px 0 4px',
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--ink4)',
                  textTransform: 'uppercase',
                  letterSpacing: '.06em',
                }}>
                  Archived / removed
                </div>
                {archivedTrades.map(trade => (
                  <SubRow
                    key={trade.id}
                    trade={trade}
                    dimmed
                    testId="subs-archived-row"
                  />
                ))}
              </>
            )}
          </>
        )}
      </div>

      {/* ── INBOX ── */}
      <div className="pm-inbox" style={{ marginBottom: 18 }}>
        {/* header */}
        <div className="pm-inbox-h">
          <div className="pm-inbox-h-l">
            Project inbox
            {pendingCount > 0 && (
              <span style={{
                background: 'var(--red)', color: 'var(--surface)',
                borderRadius: 8, padding: '1px 7px', fontSize: 9, fontWeight: 700,
              }}>
                {pendingCount}
              </span>
            )}
          </div>
          <div className="pm-inbox-h-c">
            {inboxLoading ? '…' : `${inbox.length} items`}
          </div>
        </div>

        {/* filter chips */}
        <div className="pm-inbox-filt">
          {(['all', 'pending', 'approved', 'rejected'] as InboxFilter[]).map(f => (
            <button
              key={f}
              type="button"
              className={`pm-if${inboxFilter === f ? ' on' : ''}`}
              data-testid={`inbox-filter-${f}`}
              data-active={inboxFilter === f ? 'true' : 'false'}
              onClick={() => setInboxFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
              {f === 'pending' && pendingCount > 0 && (
                <span className="pm-if-ct">{pendingCount}</span>
              )}
              {f === 'approved' && approvedCount > 0 && (
                <span className="pm-if-ct">{approvedCount}</span>
              )}
              {f === 'rejected' && rejectedCount > 0 && (
                <span className="pm-if-ct">{rejectedCount}</span>
              )}
            </button>
          ))}
        </div>

        {/* items */}
        {inboxLoading && (
          <div className="pm-ibx-i">
            <div />
            <div style={{ fontSize: 12, color: 'var(--ink4)' }}>Loading inbox…</div>
            <div />
          </div>
        )}
        {!inboxLoading && filteredInbox.length === 0 && (
          <div style={{ padding: '22px 18px', fontSize: 12, color: 'var(--ink4)', textAlign: 'center' }}>
            No items.
          </div>
        )}
        {filteredInbox.map(item => (
          <div
            key={item.id}
            className={`pm-ibx-i${statusRowClass(item.status)}`}
            data-testid={`inbox-row-${item.id}`}
            data-focus-id={`vendor_invoice:${item.id}`}
            onClick={() => onViewInboxItem(item)}
          >
            <div className={`pm-ibx-mark ${docTypeMark(item.doc_type)}`}>
              {docTypeIcon(item.doc_type)}
            </div>
            <div>
              <div className="pm-ibx-from">
                {item.company_name ?? item.trade_name ?? 'Unknown'}{' '}
                <span className={`chip ${inboxStatusChip(item).cls}`} style={{ marginLeft: 4 }}>
                  {inboxStatusChip(item).label}
                </span>
              </div>
              <div className="pm-ibx-tt">
                {item.original_name}
              </div>
              <div className="pm-ibx-st">{item.notes ?? item.doc_type.replace(/_/g, ' ')}</div>
            </div>
            <div className="pm-ibx-r">
              <div className="pm-ibx-amt">{fmtAmt(item.amount)}</div>
              <div className="pm-ibx-tm">{fmtAge(item.created_at)}</div>
              <div className="hub-row-actions-cell" style={{ marginTop: 6 }}>
                <HubDocRowActions
                  item={item}
                  testIdPrefix="inbox"
                  onEdit={actions.setEditTarget}
                  onDelete={(i) => void actions.openDelete(i)}
                />
              </div>
            </div>
          </div>
        ))}

        {inbox.length > 5 && (
          <div className="pm-ibx-more">View archived ({inbox.length - 5}) ↓</div>
        )}
      </div>

      {payRows.length > 0 && (
        <div className="panel" style={{ marginTop: 14, padding: 0, overflow: 'hidden' }} data-testid="sub-payments-report">
          <div style={{ padding: '10px 14px', fontWeight: 700, fontSize: 12, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--ink2)', borderBottom: '1px solid var(--line)' }}>
            Sub payments
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ color: 'var(--ink4)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                <th style={{ textAlign: 'left', padding: '8px 14px' }}>Sub</th>
                <th style={{ textAlign: 'left', padding: '8px 10px' }}>Status</th>
                <th style={{ textAlign: 'right', padding: '8px 10px' }}>Invoiced</th>
                <th style={{ textAlign: 'right', padding: '8px 10px' }}>Approved</th>
                <th style={{ textAlign: 'right', padding: '8px 10px' }}>Paid</th>
                <th style={{ textAlign: 'right', padding: '8px 14px' }}>Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {payRows.map(r => {
                const chip = subPaymentStatusChip(r)
                return (
                <tr key={r.sub} style={{ borderTop: '1px solid var(--line)' }}>
                  <td style={{ padding: '8px 14px', fontWeight: 600 }}>{r.sub}</td>
                  <td style={{ padding: '8px 10px' }}>
                    {chip ? <span className={`chip ${chip.cls}`}>{chip.label}</span> : '—'}
                  </td>
                  <td className="mono-c" style={{ textAlign: 'right', padding: '8px 10px' }}>{fmtAmt(String(r.invoiced))}</td>
                  <td className="mono-c" style={{ textAlign: 'right', padding: '8px 10px' }}>{fmtAmt(String(r.approved))}</td>
                  <td className="mono-c" style={{ textAlign: 'right', padding: '8px 10px', color: 'var(--green)' }}>{fmtAmt(String(r.paid))}</td>
                  <td className="mono-c" style={{ textAlign: 'right', padding: '8px 14px', fontWeight: 700, color: r.outstanding > 0 ? 'var(--amber)' : 'var(--ink3)' }}>{fmtAmt(String(r.outstanding))}</td>
                </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <ProjectVaultRow projectId={projectId} documentCount={inbox.length} />

      {/* Modals */}
      {showInviteModal && (
        <InviteSubModal
          projectId={projectId}
          onClose={() => setShowInviteModal(false)}
        />
      )}

      {actions.editTarget && (
        <DocTypeEditModal
          currentType={actions.editTarget.doc_type}
          currentExpires={actions.editTarget.insurance_expires}
          currentAmount={actions.editTarget.amount}
          fileName={actions.editTarget.original_name}
          onClose={() => actions.setEditTarget(null)}
          onSave={actions.saveEdit}
        />
      )}

      {actions.deleteTarget && actions.deleteCopy && (
        <HubDocDeleteConfirm
          fileName={actions.deleteCopy.fileName}
          body={actions.deleteCopy.body}
          subSentLine={actions.deleteCopy.subSentLine}
          busy={actions.deleteBusy}
          error={actions.deleteErr}
          onCancel={() => actions.closeDelete()}
          onConfirm={() => void actions.confirmDelete()}
        />
      )}
    </div>
  )
}
